import Image from "next/image";
import mastercardMark from "@assets/images/payment/mastercard.png";
import visaMark from "@assets/images/payment/visa.png";
import IconCheckCircle from "@assets/svg/IconCheckCircle";
import ShopMark from "@assets/svg/payment/shop.svg";
import { useEffect, useRef } from "react";

import SegmentedTabs from "@/blueprint/components/SegmentedTabs/SegmentedTabs";
import Text from "@/blueprint/components/Text/Text";
import { useTrack } from "@/analytics/events";
import { cn } from "@/lib/utils";
import { PAYMENT_TABS, type PaymentTabId } from "@/views/shop/shopContent";

/**
 * The payment tabs, as /lp/pet's: equal columns over a hairline, the picked
 * one dark with a 2px bar under it that slides along. The pick shows at the
 * checkout and in the bars (Configurator), not under the tabs, so they're a
 * choice, a radio group, rather than tabs over a panel. From `sd` they pin
 * under the chrome for the whole options column, on an opaque backing so
 * nothing shows above them; below `sd` they scroll with the options.
 */
/*
 * The marks each tab leads with: the footer's own card marks (their corners
 * in the art) at 16px tall for paying now; Shop's purple wordmark for
 * financing, taken from Shopify's own installments element, since Shop Pay
 * Installments is how Affirm financing is offered here; and the HSA/FSA
 * badge's check, a step smaller on phones. Decorative: the tab is named.
 */
const MARK = "h-[14px] w-auto shrink-0 tab:h-[16px]";
const PaymentMarks = ({ id }: { id: PaymentTabId }) => {
  if (id === "now")
    return (
      <span className="flex items-center gap-[3px]">
        <Image src={visaMark} alt="" className={MARK} />
        <Image src={mastercardMark} alt="" className={MARK} />
      </span>
    );
  if (id === "finance")
    return (
      <ShopMark
        aria-hidden
        className="h-[13px] w-[31px] shrink-0 tab:h-[15px] tab:w-[36px]"
      />
    );
  return <IconCheckCircle className="size-[16px] shrink-0 tab:size-[18px]" />;
};

/**
 * The tab row itself, marks and all, without the pinning around it: the /lp
 * pages draw the same row in their own sticky wrapper.
 */
export const PaymentTabRow = ({
  activeId,
  onChange,
  listWrapperClassName,
}: {
  activeId: PaymentTabId;
  onChange: (id: PaymentTabId) => void;
  listWrapperClassName?: string;
}) => (
  <SegmentedTabs
    usage="choice"
    variant="underline"
    tabs={PAYMENT_TABS.tabs}
    activeId={activeId}
    onChange={(next) => {
      const tab = PAYMENT_TABS.tabs.find((item) => item.id === next);
      if (tab !== undefined) onChange(tab.id);
    }}
    ariaLabel={PAYMENT_TABS.label}
    {...(listWrapperClassName === undefined ? {} : { listWrapperClassName })}
    contentFor={(tabId) => {
      const tab = PAYMENT_TABS.tabs.find((item) => item.id === tabId);
      return tab === undefined ? null : (
        <span className="flex items-center gap-[4px] tab:gap-[6px]">
          <PaymentMarks id={tab.id} />
          <Text
            variant="paragraph-sm"
            tone="inherit"
            as="span"
            // Live /lp/pet's labels: paragraph-sm, regular weight.
            className="whitespace-nowrap"
          >
            {tab.text}
          </Text>
        </span>
      );
    }}
  />
);

const PaymentTabs = ({
  activeId,
  onChange,
  className,
}: {
  activeId: PaymentTabId;
  onChange: (id: PaymentTabId) => void;
  className?: string;
}) => {
  const track = useTrack();
  const rootRef = useRef<HTMLDivElement>(null);

  // Pinned once it sits at its sticky top, read live on every scroll and
  // resize (as /lp/pet's tabs do), so a chrome height measured after mount
  // can never leave the backing off while it's pinned. Set on the element
  // itself rather than through state: the browser pins the tabs on its own
  // the moment they reach the top, and a re-render a frame or more later
  // left the options showing above them in between.
  useEffect(() => {
    const element = rootRef.current;
    if (element === null) return;
    const update = () => {
      const top = Number.parseFloat(getComputedStyle(element).top);
      element.toggleAttribute(
        "data-pinned",
        Number.isFinite(top) &&
          element.getBoundingClientRect().top <= top + 1 &&
          window.scrollY > 0,
      );
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const select = (id: PaymentTabId) => {
    if (id === activeId) return;
    onChange(id);
    track("payment_tab_selected", { tab_id: id });
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        "bg-surface-page sd:sticky sd:top-[var(--zone-top-side,172px)] sd:z-10",
        "sd:data-[pinned]:before:pointer-events-none sd:data-[pinned]:before:absolute sd:data-[pinned]:before:inset-x-0 sd:data-[pinned]:before:bottom-full sd:data-[pinned]:before:h-[var(--zone-top-side,172px)] sd:data-[pinned]:before:bg-surface-page sd:data-[pinned]:before:content-['']",
        className,
      )}
    >
      <PaymentTabRow activeId={activeId} onChange={select} />
    </div>
  );
};

export default PaymentTabs;
